import React, { useContext, useEffect, useState } from 'react';
import { Table, Modal, Form, Input, InputNumber, Space, notification } from 'antd';
import { PlusCircleTwoTone } from '@ant-design/icons';
import "./style.css"
import UploadBanners from '../UploadBanners';
import { Context } from '../../context';

const { Column } = Table;

const ManageAds = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('Add');
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { token, apiUrl } = useContext(Context);
  const [advertisementsList, setAdvertisementsList] = useState([]);
  const [advertisementId, setAdvertisementId] = useState("");

  useEffect(() => {
    const fetchAdvertisementsList = async () => {
      try {
        const data = await fetch(`${apiUrl}/advertisements/all`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        const response = await data.json();
        setAdvertisementsList(response?.Advertisements);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchAdvertisementsList();
  }, [apiUrl, token]);

  const showEditModal = (record) => {
    setModalVisible(true);
    setModalType('Edit');
    setAdvertisementId(record?._id);
    form.setFieldsValue(record);
  };

  const handleSubmit = async () => {
    setConfirmLoading(true);
    try {
      const method = modalType === "Add" ? "POST" : "PUT";
      const url = modalType === "Add" ? `${apiUrl}/advertisements` : `${apiUrl}/advertisements/${advertisementId}`;
      const values = await form.validateFields();
      const postAd = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(values),
      });

      const response = await postAd.json();

      if (response.success) {
        notification.success({
          message: response?.success,
          description: response?.message,
          duration: 2
        });

        setModalVisible(false);
        form.resetFields();

        const data = await fetch(`${apiUrl}/advertisements/all`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        const updatedResponse = await data.json();
        setAdvertisementsList(updatedResponse?.Advertisements);
      } else {

        notification.error({
          message: "Failure",
          description: response.detail,
          duration: 2
        });
      }
    } catch (error) {
      notification.error({
        message: "Submission Failed",
        description: "An error occurred while submitting the form.",
        duration: 2
      });
    }
    setConfirmLoading(false);
  };

  const showConfirmDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this advertisement?",
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await fetch(`${apiUrl}/advertisements/${record._id}`, {
            method: "DELETE",
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });
          const deleteResponse = await response.json();

          if (deleteResponse.success) {
            notification.success({
              message: "Success",
              description: "Advertisement deleted successfully",
              duration: 2
            });

            const data = await fetch(`${apiUrl}/advertisements/all`, {
              method: "GET",
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
            });
            const updatedResponse = await data.json();
            setAdvertisementsList(updatedResponse?.Advertisements);
          } else {
            notification.error({
              message: "Delete Failed",
              description: deleteResponse.message || "Unable to delete the advertisement.",
              duration: 2
            });
          }
        } catch (error) {
          console.error("Error deleting advertisement:", error);
          notification.error({
            message: "Error",
            description: "An error occurred while deleting the advertisement.",
            duration: 2
          });
        }
      },
    });
  };

  return (
    <div className="manage-ads-wrapper" style={{ padding: "25px" }}>
      <Table dataSource={advertisementsList}>
        <Column title="Title" dataIndex="title" key="title" align="center" />
        <Column title="Position" dataIndex="position" key="position" align="center" />
        <Column
          title="Banner"
          dataIndex="desktopBannerAd"
          key="desktopBannerAd"
          align="center"
          render={(value) => (
            <img src={value + "/public"} alt="banner" style={{ width: '250px' }} />
          )}
        />
        <Column
          title="Actions"
          key="action"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <a onClick={() => showEditModal(record)}>Edit</a>
              <a onClick={() => showConfirmDelete(record)}>Delete</a>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={`${modalType} Ad`}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={confirmLoading}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please input the title!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="position" label="Position" rules={[{ required: true, message: 'Please input the position!' }]}>
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item
            name="mobileBannerAd"
            label="Mobile Banner"
            rules={[{ required: true, message: 'Please upload the Mobile Banner!' }]}
          >
            <UploadBanners name={"mobileBannerAd"} updateForm={form} />
          </Form.Item>
          <Form.Item
            name="mobileModalAd"
            label="Mobile Modal"
            rules={[{ required: true, message: 'Please upload the Mobile Modal!' }]}
          >
            <UploadBanners name={"mobileModalAd"} updateForm={form} />
          </Form.Item>
          <Form.Item
            name="desktopBannerAd"
            label="Desktop Banner"
            rules={[{ required: true, message: 'Please upload the Desktop Banner!' }]}
          >
            <UploadBanners name={"desktopBannerAd"} updateForm={form} />
          </Form.Item>
          <Form.Item
            name="desktopModalAd"
            label="Desktop Modal"
            rules={[{ required: true, message: 'Please upload the Desktop Modal!' }]}
          >
            <UploadBanners name={"desktopModalAd"} updateForm={form} />
          </Form.Item>

          <Form.Item
            name="redirectLink"
            label="Redirect URL"
            rules={[{ type: 'url', message: 'Please enter a valid URL!' }]}
          >
            <Input placeholder="Enter redirect URL" />
          </Form.Item>

        </Form>
      </Modal>

      <div className="add-series-btn">
        <PlusCircleTwoTone
          twoToneColor="#FF0091"
          style={{ fontSize: '48px' }}
          onClick={() => {
            setModalVisible(true);
            setModalType('Add');
          }}
        />
      </div>
    </div>
  );
};

export default ManageAds;
